import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import useDocument from "../../hooks/useDocument";

import DocumentModal from "../modals/DocumentModal";

import Page from "./Page";
import Pagination from "../elements/Pagination";
import Loading from "../ui/Loading";
import { Box, Typography } from "@mui/material";

import DocumentViewerModal from "../modals/DocumentViewerModal";

const Documents = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    getDocuments,
    documents,
    recordCount,
    getDocumentUrl,
    documentUrl,
    selectDocument,
    selectedDocument,
  } = useDocument();

  const [openDocumentModal, setOpenDocumentModal] = useState(false);
  const [openViewer, setOpenViewer] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [filter, setFilter] = useState({ status: "open" });

  const scope = location.state.scope;
  const id = location.state.id;
  const name = location.state.name;
  const returnUrl = location.state.returnUrl;
  const returnState = location.state.returnState;

  useEffect(() => {
    getData();
  }, [scope, id]);

  const getData = async () => {
    await getDocuments(scope, id);
  };

  const handleSearch = async () => {
    setPageNumber(0);
    // await getPage(pageNumber, recordsPerPage, filter);
  };

  const handleClearFilter = async () => {
    setFilter(null);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handleRecordsPerPageChange = (newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
  };

  const handleToolbarItemClick = (item) => {
    switch (item) {
      case "add":
        setOpenDocumentModal(true);
        break;

      case "back":
        navigate(returnUrl, { state: returnState });
        break;
    }
  };

  const handleCloseModal = () => {
    setOpenDocumentModal(false);
    getData();
  };

  const handleRowClick = async (column, id) => {
    await selectDocument(id);
    setOpenViewer(true);
  };

  const handleViewerClose = (result) => {
    setOpenViewer(false);

    const shouldRefresh =
      result === true ||
      (typeof result === "object" && (result.deleted || result.updated));

    if (shouldRefresh) {
      getData(); // Refresh list if document was deleted
    }
  };

  const handleSave = async (returnData) => {
    setOpenDocumentModal(false);
    if (returnData) {
      setFilter(returnData.filter);
      setPageNumber(returnData.pageNumber);
    }
  };

  if (!documents) return <Loading />;

  const splitFolderPath = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return [];

    return raw
      .split(/[\\/]+/)
      .map((segment) => segment.trim())
      .filter(Boolean);
  };

  const tree = { children: {}, docs: [], total: 0, directCount: 0 };

  for (const doc of documents) {
    const segments = splitFolderPath(doc?.folderPath || doc?.category || "");
    let cursor = tree;

    for (const segment of segments) {
      if (!cursor.children[segment]) {
        cursor.children[segment] = {
          name: segment,
          children: {},
          docs: [],
          total: 0,
          directCount: 0,
        };
      }
      cursor = cursor.children[segment];
    }

    cursor.docs.push(doc);
  }

  const computeTotals = (node) => {
    const childTotal = Object.values(node.children || {}).reduce(
      (sum, child) => sum + computeTotals(child),
      0,
    );
    node.docs.sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
      }),
    );
    node.directCount = node.docs.length;
    node.total = node.docs.length + childTotal;
    return node.total;
  };

  computeTotals(tree);

  const renderDocRow = (doc, depth) => (
    <Box
      key={doc?.id || doc?._id || doc?.key}
      sx={{ pl: depth * 2, py: 0.5 }}
    >
      <Typography
        variant="body2"
        sx={{ cursor: "pointer", textDecoration: "underline" }}
        onClick={() => handleRowClick("name", doc.id || doc._id || doc.key)}
      >
        {doc?.name || doc?.originalName || "Untitled document"}
      </Typography>
    </Box>
  );

  const renderNode = (node, depth = 0, label = "Root") => {
    const childNames = Object.keys(node.children || {}).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    return (
      <Box key={`${label}-${depth}`} sx={{ mt: depth === 0 ? 2 : 1 }}>
        <Typography
          variant={depth === 0 ? "subtitle1" : "subtitle2"}
          sx={{ fontWeight: 700, pl: depth * 2 }}
        >
          {`${label} (${node.directCount || 0})`}
        </Typography>

        {node.docs.map((doc) => renderDocRow(doc, depth + 1))}

        {childNames.map((childName) =>
          renderNode(node.children[childName], depth + 1, childName),
        )}
      </Box>
    );
  };

  return (
    <>
      <Page
        pageName="Documents"
        entityName={name}
        onSearch={handleSearch}
        onToolItemClick={handleToolbarItemClick}
        includeToolbar
        items={[
          {
            name: "back",
            iconName: "ArrowCircleLeft",
            hide: scope === "global",
          },
          { name: "add", iconName: "AddCircleOutline" },
        ]}
      >
        <Pagination
          totalCount={recordCount}
          styles={{ marginTop: "5px" }}
          pageNumber={pageNumber}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsPerPageChange}
        />

        {renderNode(tree, 0, "Root")}
      </Page>

      {openDocumentModal && (
        <DocumentModal
          open={openDocumentModal}
          onClose={handleCloseModal}
          scope={scope}
          id={id}
        />
      )}

      {openViewer && documentUrl && (
        <DocumentViewerModal
          open={openViewer}
          onClose={handleViewerClose}
          fileUrl={documentUrl}
          document={selectedDocument}
        />
      )}
    </>
  );
};

export default Documents;
